// ============================================================================
// Permission model — SINGLE SOURCE OF TRUTH.
// Ported verbatim from design-reference/src/data.jsx (can / visible*).
// The API treats these as authoritative (enforced server-side on every route);
// the web app imports the same functions to gate UI only.
// ============================================================================
import { EVERYONE_TEAM_ID } from './constants.js';
import type { PermissionUser, Project, Task } from './types.js';

export type PermissionAction =
  | 'manage'
  | 'manageUsers'
  | 'approveTimeOff'
  | 'viewAudit'
  | 'editEntryMeta'
  | 'editEntryTime'
  | 'deleteEntry';

/** The minimal entry shape needed for entry-scoped permission checks. */
export interface EntryRef {
  userId: string;
}

export function can(user: PermissionUser, action: PermissionAction, entry?: EntryRef): boolean {
  switch (action) {
    case 'manage':
      return user.role === 'Admin' || user.role === 'Manager';
    case 'manageUsers':
      return user.role === 'Admin';
    case 'approveTimeOff':
      return user.role === 'Admin';
    case 'viewAudit':
      return user.role === 'Admin';
    case 'editEntryMeta':
      // own, or Admin for anyone
      return !!entry && (entry.userId === user.id || user.role === 'Admin');
    case 'editEntryTime':
      // any entry, Admin only
      return user.role === 'Admin';
    case 'deleteEntry':
      // own only, all roles
      return !!entry && entry.userId === user.id;
    default:
      return false;
  }
}

/** Projects a user may select/see. Members are restricted to their teams + Everyone. */
export function visibleProjects<P extends Pick<Project, 'teamIds' | 'archived'>>(
  projects: P[],
  user: PermissionUser,
): P[] {
  const live = projects.filter((p) => !p.archived);
  if (user.role !== 'Member') return live;
  return live.filter(
    (p) =>
      p.teamIds.includes(EVERYONE_TEAM_ID) ||
      p.teamIds.some((tid) => user.teamIds.includes(tid)),
  );
}

/** Tasks in a project a user may select/see (same team rule as projects). */
export function visibleTasks<T extends Pick<Task, 'projectId' | 'teamIds' | 'archived'>>(
  tasks: T[],
  user: PermissionUser,
  projectId: string,
): T[] {
  return tasks.filter(
    (t) =>
      t.projectId === projectId &&
      !t.archived &&
      (user.role !== 'Member' ||
        t.teamIds.includes(EVERYONE_TEAM_ID) ||
        t.teamIds.some((tid) => user.teamIds.includes(tid))),
  );
}

/** Users a viewer may see in Dashboard/Reports. Members see self + teammates. */
export function visibleUsers<U extends Pick<PermissionUser, 'id' | 'teamIds'>>(
  users: U[],
  user: PermissionUser,
): U[] {
  if (user.role !== 'Member') return users;
  return users.filter(
    (u) =>
      u.id === user.id ||
      u.teamIds.some((tid) => tid !== EVERYONE_TEAM_ID && user.teamIds.includes(tid)),
  );
}

/** True if `viewer` is allowed to see `target` (used to scope reads). */
export function canSeeUser(viewer: PermissionUser, target: Pick<PermissionUser, 'id' | 'teamIds'>): boolean {
  if (viewer.role !== 'Member') return true;
  return (
    target.id === viewer.id ||
    target.teamIds.some((tid) => tid !== EVERYONE_TEAM_ID && viewer.teamIds.includes(tid))
  );
}
